import styled from 'styled-components';
import { Industry } from 'Types/IndustryTypes';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { formatNumberWithCommas, valueToObject } from 'Utils/UtilityFunctions';
import {
  maxMobile, maxTablet, maxWidth, minExtraLargeScreen, primaryFont, retina,
  textBlack,
  wMLight,
} from 'Utils/utilsStyle';
import GlobalButton from 'Components/common/components/GlobalButton';
import { useDispatch, useSelector } from 'react-redux';
import {
  FEED_GILROY_ROUTE, INDUSTRY_NEWS_ROUTE, SEARCH, SEARCH_ROUTE,
} from 'Utils/Constants';
import { actions as flurryActions } from 'Slice/HelperSlice';
import { actions } from 'Slice/IndustriesSlice';
import { actions as GlobalSearchActions } from 'Slice/GlobalSearchResultsSlice';
import { selectIsPagination } from 'Selector/GlobalSearchResultsSelector';
import CompanyListViewSkeleton from 'Components/common/skeleton/CompanyListViewSkeleton';
import { SearchResultsRouteState } from 'Pages/search/GlobalSearchResults';
import { Tooltip } from 'antd';
import HeadingWithShowAll from './HeadingWithShowAll';
import SectionHeading from './SectionHeading';

type Props = {
  heading: string,
  industriesCollection?: Industry[]
  loading?: boolean,
  industryCount?: Industry[],
  count?: number,
  showUnfollow?: boolean,
  inGlobalSearch?: boolean,
  isRecommended?: boolean,
  query?: string,
} & typeof defaultProps;

const defaultProps = {
  loading: false,
  industriesCollection: [] as Industry[],
  industryCount: [] as Industry[],
  count: 0,
  showUnfollow: false,
  inGlobalSearch: false,
  isRecommended: false,
  query: '',
};

const StyleWrapper = styled.div`
width: 100%;
.industry-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 0px;
  cursor: pointer;
  @media all and (min-width: ${minExtraLargeScreen}) {
    padding: 10px 0px;
  }
  @media all and (max-width: ${maxWidth}), ${retina} {
    padding: 6px 0px;
  }
  @media all and (max-width: ${maxTablet}) {
    padding: 4px 0px;
  }
  @media all and (max-width: ${maxMobile}) {
    padding: 4px 0px;
  }
  &:hover {
    background: #FFF;
    border-radius: 6px;
  }
  .industry-info {
    display: flex;
    column-gap: 10px;
    align-items: center;
    max-width: calc(100% - 84px);
    @media all and (min-width: ${minExtraLargeScreen}) {
      column-gap: 14px;
    }
    @media all and (max-width: ${maxWidth}), ${retina} {
      column-gap: 10px;
    }
    @media all and (max-width: ${maxTablet}) {
      column-gap: 6px;
    }
    @media all and (max-width: ${maxMobile}) {
      column-gap: 6px;
    }
    .industry-img {
      width: 48px;
      height: 42px;
      border: 2px solid #D7D3C9;
      border-radius: 6px;
      position: relative;
      @media all and (min-width: ${minExtraLargeScreen}) {
        width: 62px;
        height: 54px;
        border-radius: 6px;
        border: 3px solid #D7D3C9;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        width: 48px;
        height: 42px;
        border-radius: 4px;
      }
      @media all and (max-width: ${maxTablet}) {
        width: 38px;
        height: 34px;
        border-radius: 3px;
        border: 2px solid #D7D3C9;
      }
      @media all and (max-width: ${maxMobile}) {
        width: 34px;
        height: 30px;
        border-radius: 4px;
        border: 2px solid #D7D3C9;
      }
      img {
        width: 100%;
        height: 100%;
        border-radius: 4px;
        position:absolute;
        @media all and (max-width: ${maxWidth}), ${retina} {
          border-radius: 2px;
        }
        @media all and (max-width: ${maxTablet}) {
          border-radius: 2px;
        }
      }
    }
    .industry-name {
      width: calc(100% - 48px);
      color: ${textBlack};
      font-family: ${primaryFont};
      font-size: 16px;
      font-weight: ${wMLight};
      line-height: 18px;
      letter-spacing: -0.52px;
      display: inline-block;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
      &.triggers-bullet-tooltip {
        &.person-designation-tip {
          .ant-tooltip {
            .ant-tooltip-content {
              .ant-tooltip-inner {
                white-space: normal;
              }
            }
          }
        }
      }
      @media all and (min-width: ${minExtraLargeScreen}) {
        width: calc(100% - 62px);
        font-size: 22px;
        line-height: 23px;
        letter-spacing: -0.99px;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        width: calc(100% - 48px);
      }
      @media all and (max-width: ${maxTablet}) {
        width: calc(100% - 38px);
        font-size: 14px;
        line-height: 16px;
        letter-spacing: -0.336px;
      }
      @media all and (max-width: ${maxMobile}) {
        width: calc(100% - 34px);
        font-size: 12px;
        line-height: 14px;
        letter-spacing: -0.396px;
      }
    }
  }
  .global-btn-industries {
    margin-top: 0px;
    .button-follow {
      display: inline-flex;
      justify-content: center;
      align-items: center;
      height: 24px;
      width: 84px;
      border-radius: 16px;
      padding: 4px 14px;
      text-align: center;
      font-family: ${primaryFont};
      font-size: 14px;
      font-weight: ${wMLight};
      line-height: 17px;
      border: 1px solid #001760;
      @media all and (min-width: ${minExtraLargeScreen}) {
        height: 34px;
        width: 120px;
        padding: 6px 20px;
        border-radius: 22px;
        font-size: 20px;
        line-height: 23px;
      }
      @media all and (max-width: ${maxWidth}), ${retina} {
        height: 24px;
        width: 84px;
        border-radius: 16px;
        font-size: 14px;
        line-height: 17px;
        padding: 4px 14px;
      }
      @media all and (max-width: ${maxTablet}) {
        height: 20px;
        width: 72px;
        padding: 3px 12px;
        border-radius: 16px;
        font-size: 12px;
        line-height: 15px;
      }
      @media all and (max-width: ${maxMobile}) {
        height: 22px;
        width: 64px;
        border-radius: 18px;
        font-size: 12px;
        line-height: 15px;
      }
    }
  }
}
`;

const IndustriesSectionListView = function IndustriesSectionListView(props: Props) {
  const [searchParams] = useSearchParams();
  const extendedSections = ['person', 'company', 'article', 'industry'];
  const extendedSection = (searchParams.get('extend') ?? '');
  const isDetailedSearch: boolean = extendedSections.includes(extendedSection);
  const {
    heading, loading,
    industriesCollection, count, industryCount,
    showUnfollow,
    inGlobalSearch,
    isRecommended,
    query,
  } = props;

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const host = window.location.href;
  const { pathname } = useLocation();
  const isSearchPage = pathname.includes(SEARCH);
  const isPagination: boolean = useSelector(selectIsPagination);
  const formattedCount = formatNumberWithCommas(count);
  const location = useLocation();
  const { state } = location as { state: SearchResultsRouteState };
  const previousPath = (state)?.previousPath ?? FEED_GILROY_ROUTE;

  const handleCardClicked = (industry: Industry) => {
    const industryURL = `${INDUSTRY_NEWS_ROUTE}${industry?.id}`;
    navigate(industryURL);
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('open_industry_view', 0, {
        industry_id: industry?.id,
        host,
      }),
    ));

    if (isSearchPage) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('searchPage_industry_clicked', 0, {
          industry_id: industry?.id,
          industry_name: industry?.name,
          host,
        }),
      ));
    }
  };

  const followIndustry = (industry: Industry) => {
    dispatch(actions.addingIndustry(industry));
    dispatch(GlobalSearchActions.searchIndustryFollowLoading(industry.id));
  };

  const unFollowingIndustry = (industry: Industry[]) => {
    dispatch(actions.removingIndustries(industry));
  };

  const followingIndustry = (industry: Industry) => {
    followIndustry(industry);
    if (isSearchPage) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('searchPage_indsutry_follow', 0, {
          industry_id: industry?.id,
          industry_name: industry?.name,
          host,
        }),
      ));
    }
    if (isRecommended) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('follow_industries_recommendedIndustries', 0, {
          industry_id: industry?.id,
          host,
        }),
      ));
    }
  };

  const unfollow = (industry: Industry) => {
    unFollowingIndustry([industry]);
    if (isSearchPage) {
      dispatch(flurryActions.callFlurryEvent(
        valueToObject('searchPage_indsutry_unfollow', 0, {
          industry_id: industry?.id,
          industry_name: industry?.name,
          host,
        }),
      ));
    }
  };

  const createIndustriesRoute = () => {
    dispatch(flurryActions.callFlurryEvent(
      valueToObject('searchPage_industries_see_all_clicked', 0),
    ));
    window.scrollTo(0, 0);
    return navigate(`${pathname}?query=${query}&extend=industry`, { state: { enableBackButton: true, previousPath } });
  };

  const navigateToSearchResult = () => {
    const path = `${SEARCH_ROUTE}${query}`;
    navigate(path, {
      state: isDetailedSearch ? { enableBackButton: true, previousPath } : { previousPath },
    });
  };

  return (
    industriesCollection.length > 0 || loading ? (
      <StyleWrapper>
        {
          loading && !isDetailedSearch ? (<CompanyListViewSkeleton />)
            : (
              <>
                <HeadingWithShowAll
                  inGlobalSearch={inGlobalSearch}
                  isShowAllHidden={isDetailedSearch ? true
                    : industryCount && industryCount.length <= 2}
                  leftHeading={(
                    <SectionHeading>{`${heading} (${formattedCount})`}</SectionHeading>)}
                  onClick={createIndustriesRoute}
                  onSeelessClick={navigateToSearchResult}
                  isDetailedSearch={isDetailedSearch}
                />
                {
                  industriesCollection?.map((industry) => (
                    <div
                      className="industry-container"
                      onClick={() => handleCardClicked(industry)}
                      onKeyDown={() => handleCardClicked(industry)}
                      role="button"
                      tabIndex={0}
                    >
                      <div className="industry-info">
                        <div className="industry-img">
                          <img src={industry.image} alt="ind-img" />
                        </div>
                        <Tooltip
                          trigger="hover"
                          placement="top"
                          getPopupContainer={(e: HTMLElement) => e}
                          className="triggers-bullet-tooltip person-designation-tip"
                          color="#fff"
                          title={industry.name}
                          arrow={false}
                        >
                          <div className="industry-name">
                            <span>{industry?.name}</span>
                          </div>
                        </Tooltip>
                      </div>
                      {
                        !industry.added && (
                          <div
                            className="global-btn-industries"
                            role="button"
                            tabIndex={0}
                            aria-label="button"
                            onClick={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          >
                            <GlobalButton
                              title={industry.loading ? 'Following' : 'Follow'}
                              textColor="#ffffff"
                              bgColor="#001760"
                              borderRadius="25px"
                              innerSpace="8px 20px"
                              PaddingSm="4px 16px 3px 16px"
                              buttonClassName="button-follow"
                              disabled={industry.loading}
                              onButtonClick={() => followingIndustry(industry)}
                            />
                          </div>
                        )
                      }
                      {
                        industry.added && showUnfollow ? (
                          <div
                            className="global-btn-industries"
                            role="button"
                            tabIndex={0}
                            aria-label="button"
                            onClick={(e) => e.stopPropagation()}
                            onKeyPress={(e) => e.stopPropagation()}
                          >
                            <GlobalButton
                              title="Unfollow"
                              textColor="#001760"
                              bgColor="transparent"
                              borderRadius="25px"
                              innerSpace="8px 20px"
                              PaddingSm="4px 16px 3px 16px"
                              buttonClassName="button-follow"
                              onButtonClick={() => unfollow(industry)}
                            />
                          </div>
                        ) : null
                      }
                    </div>
                  ))
                }
                {isDetailedSearch && isPagination && <CompanyListViewSkeleton />}
              </>
            )
        }
      </StyleWrapper>
    ) : (<> </>)
  );
};
IndustriesSectionListView.defaultProps = defaultProps;
export default IndustriesSectionListView;
